<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ServiceOrdersAPI from 'dashboard/api/serviceOrders';
import ResolveTicketModal from 'dashboard/routes/dashboard/tickets/ResolveTicketModal.vue';
import TicketAttachments from 'dashboard/components/tickets/TicketAttachments.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['back', 'update']);

const { t } = useI18n();

const { isCloudFeatureEnabled } = useAccount();
const isContractsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CONTRACTS)
);
const router = useRouter();
const store = useStore();

// Agents and teams
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const currentUser = useMapGetter('getCurrentUser');

// Activity state
const newComment = ref('');
const isPrivate = ref(false);
const isSubmitting = ref(false);
let typingTimer = null;
const isUpdatingAssignee = ref(false);
const isUpdatingTeam = ref(false);
const isUpdatingStatus = ref(false);
const isUpdatingPriority = ref(false);
const showResolveModal = ref(false);
const serviceOrders = ref([]);

// Local state for editable fields (syncs with props but updates locally on change)
const currentStatus = ref(null);
const currentPriority = ref(null);
const currentAssigneeId = ref(null);
const currentTeamId = ref(null);

// Status and priority options
const statusOptions = [
  { value: 'open', label: 'TICKETS.STATUS.OPEN' },
  { value: 'in_progress', label: 'TICKETS.STATUS.IN_PROGRESS' },
  { value: 'escalated', label: 'TICKETS.STATUS.ESCALATED' },
  { value: 'waiting_client', label: 'TICKETS.STATUS.WAITING_CLIENT' },
  { value: 'waiting_internal', label: 'TICKETS.STATUS.WAITING_INTERNAL' },
];

const priorityOptions = [
  { value: 'low', label: 'TICKETS.PRIORITY.LOW' },
  { value: 'medium', label: 'TICKETS.PRIORITY.MEDIUM' },
  { value: 'high', label: 'TICKETS.PRIORITY.HIGH' },
  { value: 'urgent', label: 'TICKETS.PRIORITY.URGENT' },
];

const ticketConversations = computed(() => {
  return store.getters['ticketConversations/getByTicketId'](
    props.ticket?.display_id
  );
});

const isLoadingActivity = computed(() => {
  return store.getters['ticketConversations/getUIFlags'].isFetching;
});

const typingUsers = computed(() => {
  return store.getters['ticketTypingStatus/getUserList'](
    props.ticket?.display_id
  );
});

const typingUserNames = computed(() => {
  if (!typingUsers.value.length) return '';
  const names = typingUsers.value.map(u => u.name);
  if (names.length === 1) return `${names[0]} ${t('TICKETS.TYPING.IS_TYPING')}`;
  return `${names.join(', ')} ${t('TICKETS.TYPING.ARE_TYPING')}`;
});

const isTicketClosed = computed(() => {
  return ['resolved', 'closed', 'cancelled'].includes(props.ticket?.status);
});

const canResolve = computed(() => {
  return !['resolved', 'closed', 'cancelled'].includes(props.ticket?.status);
});

const isVerificationPending = computed(() => {
  return props.ticket?.verification_status === 'pending';
});

const verificationStatusLabel = computed(() => {
  const status = props.ticket?.verification_status;
  if (!status || status === 'not_required') return null;
  const map = {
    pending: t('TICKETS.VERIFICATION.STATUS.PENDING'),
    verified: t('TICKETS.VERIFICATION.STATUS.VERIFIED'),
    rejected: t('TICKETS.VERIFICATION.STATUS.REJECTED'),
  };
  return map[status];
});

const verificationStatusClasses = computed(() => {
  const status = props.ticket?.verification_status;
  const map = {
    pending: 'bg-n-amber-9/20 text-n-amber-11',
    verified: 'bg-n-teal-9/20 text-n-teal-11',
    rejected: 'bg-n-ruby-9/20 text-n-ruby-11',
  };
  return map[status] || '';
});

const sendTypingOn = () => {
  store.dispatch('ticketTypingStatus/toggleTyping', {
    ticketId: props.ticket?.display_id,
    status: 'on',
    isPrivate: isPrivate.value,
  });
};

const sendTypingOff = () => {
  store.dispatch('ticketTypingStatus/toggleTyping', {
    ticketId: props.ticket?.display_id,
    status: 'off',
    isPrivate: isPrivate.value,
  });
};

const onCommentInput = () => {
  if (typingTimer) clearTimeout(typingTimer);
  sendTypingOn();
  typingTimer = setTimeout(() => {
    sendTypingOff();
  }, 4000);
};

// Load agents and teams on mount (needed for @ mention suggestions)
onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

onBeforeUnmount(() => {
  if (typingTimer) clearTimeout(typingTimer);
  if (props.ticket?.display_id) {
    sendTypingOff();
  }
});

const fetchConversations = async () => {
  if (!props.ticket?.display_id) return;
  await store.dispatch('ticketConversations/fetch', props.ticket.display_id);
};

const addComment = async () => {
  if (!newComment.value.trim() || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    sendTypingOff();
    await store.dispatch('ticketConversations/create', {
      ticketId: props.ticket.display_id,
      message: newComment.value,
      isPrivate: isPrivate.value,
    });
    newComment.value = '';
    isPrivate.value = false;
  } catch (error) {
    useAlert(t('TICKETS.COMMENT_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const formatDateTime = date => {
  if (!date) return '-';
  return new Date(date).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const updateAssignee = async assigneeId => {
  if (isUpdatingAssignee.value) return;

  isUpdatingAssignee.value = true;
  try {
    await store.dispatch('tickets/assignTicket', {
      id: props.ticket.display_id,
      assigneeId: assigneeId || null,
    });
    currentAssigneeId.value = assigneeId || null;
    emit('update', { assignee_id: assigneeId });
    useAlert(t('TICKETS.SIDEBAR.ASSIGNEE_UPDATED'));
  } catch (error) {
    useAlert(t('TICKETS.SIDEBAR.ASSIGNEE_UPDATE_ERROR'));
  } finally {
    isUpdatingAssignee.value = false;
  }
};

const updateTeam = async teamId => {
  if (isUpdatingTeam.value) return;

  isUpdatingTeam.value = true;
  try {
    const teamIdNum = teamId ? parseInt(teamId, 10) : null;
    await store.dispatch('tickets/updateTicket', {
      id: props.ticket.display_id,
      team_id: teamIdNum,
    });
    currentTeamId.value = teamIdNum;
    emit('update', { team_id: teamIdNum });
  } finally {
    isUpdatingTeam.value = false;
  }
};

const updateStatus = async newStatus => {
  if (isUpdatingStatus.value) return;

  isUpdatingStatus.value = true;
  try {
    await store.dispatch('tickets/updateTicket', {
      id: props.ticket.display_id,
      status: newStatus,
    });
    currentStatus.value = newStatus;
    emit('update', { status: newStatus });
    useAlert(t('TICKETS.SIDEBAR.STATUS_UPDATED'));
  } catch (error) {
    useAlert(t('TICKETS.SIDEBAR.STATUS_UPDATE_ERROR'));
  } finally {
    isUpdatingStatus.value = false;
  }
};

const updatePriority = async newPriority => {
  if (isUpdatingPriority.value) return;

  isUpdatingPriority.value = true;
  try {
    await store.dispatch('tickets/updateTicket', {
      id: props.ticket.display_id,
      priority: newPriority,
    });
    currentPriority.value = newPriority;
    emit('update', { priority: newPriority });
    useAlert(t('TICKETS.SIDEBAR.PRIORITY_UPDATED'));
  } catch (error) {
    useAlert(t('TICKETS.SIDEBAR.PRIORITY_UPDATE_ERROR'));
  } finally {
    isUpdatingPriority.value = false;
  }
};

const fetchServiceOrders = async () => {
  const ticketFolio = props.ticket?.folio;
  if (!ticketFolio) return;

  try {
    const response = await ServiceOrdersAPI.get({
      ticket_folio: ticketFolio,
    });
    serviceOrders.value = response.data.payload || [];
  } catch (error) {
    serviceOrders.value = [];
  }
};

const openResolveModal = () => {
  // Check for pending service orders before opening resolve modal
  const hasPendingOrders =
    serviceOrders.value?.some(
      order =>
        !['field_complete', 'redirected', 'cancelled', 'merged'].includes(
          order.status
        )
    ) || false;

  if (hasPendingOrders) {
    useAlert(t('TICKETS.SIDEBAR.PENDING_ORDERS_ERROR'));
    return;
  }

  showResolveModal.value = true;
};

const onResolved = () => {
  showResolveModal.value = false;
  emit('update', { status: 'resolved' });
};

const verifyTicket = async () => {
  try {
    await store.dispatch('tickets/verifyTicket', {
      id: props.ticket.display_id,
    });
    emit('update', { verification_status: 'verified' });
    useAlert(t('TICKETS.VERIFICATION.VERIFIED_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.VERIFICATION.VERIFY_ERROR'));
  }
};

const rejectVerification = async () => {
  try {
    await store.dispatch('tickets/rejectVerification', {
      id: props.ticket.display_id,
    });
    emit('update', { verification_status: 'rejected' });
    useAlert(t('TICKETS.VERIFICATION.REJECTED_SUCCESS'));
  } catch (error) {
    useAlert(t('TICKETS.VERIFICATION.REJECT_ERROR'));
  }
};

// Sync local state and fetch data when ticket changes
watch(
  () => props.ticket,
  newTicket => {
    if (newTicket) {
      currentStatus.value = newTicket.status;
      currentPriority.value = newTicket.priority;
      currentAssigneeId.value = newTicket.assignee_id;
      currentTeamId.value = newTicket.team_id;
      fetchConversations();
      fetchServiceOrders();
    }
  },
  { immediate: true, deep: true }
);

const isOverdue = computed(() => {
  if (!props.ticket?.sla_deadline) return false;
  return new Date(props.ticket.sla_deadline) < new Date();
});

const statusConfig = computed(() => {
  const configs = {
    open: {
      label: t('TICKETS.STATUS.OPEN'),
      classes: 'bg-n-amber-9/20 text-n-amber-11',
    },
    in_progress: {
      label: t('TICKETS.STATUS.IN_PROGRESS'),
      classes: 'bg-n-brand/20 text-n-blue-text',
    },
    escalated: {
      label: t('TICKETS.STATUS.ESCALATED'),
      classes: 'bg-n-ruby-9/20 text-n-ruby-11',
    },
    waiting_client: {
      label: t('TICKETS.STATUS.WAITING_CLIENT'),
      classes: 'bg-orange-100 text-orange-800',
    },
    waiting_internal: {
      label: t('TICKETS.STATUS.WAITING_INTERNAL'),
      classes: 'bg-purple-100 text-purple-800',
    },
    resolved: {
      label: t('TICKETS.STATUS.RESOLVED'),
      classes: 'bg-n-teal-9/20 text-n-teal-11',
    },
    closed: {
      label: t('TICKETS.STATUS.CLOSED'),
      classes: 'bg-n-slate-9/20 text-n-slate-11',
    },
    cancelled: {
      label: t('TICKETS.STATUS.CANCELLED'),
      classes: 'bg-n-slate-9/20 text-n-slate-11',
    },
  };
  return configs[props.ticket?.status] || configs.open;
});

const channelIcon = computed(() => {
  const icons = {
    whatsapp: 'i-lucide-message-circle',
    web_chat: 'i-lucide-globe',
    email: 'i-lucide-mail',
    phone: 'i-lucide-phone',
    in_person: 'i-lucide-user',
    mobile_app: 'i-lucide-smartphone',
    app_cea: 'i-lucide-smartphone',
    whatsapp_gob: 'i-lucide-message-circle',
  };
  return icons[props.ticket?.channel] || 'i-lucide-message-square';
});

const formatShortDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const goBack = () => {
  emit('back');
};

const viewFullDetails = () => {
  router.push({
    name: 'ticket_details',
    params: { ticketId: props.ticket.display_id },
  });
};
</script>

<template>
  <div class="flex flex-col h-full w-full">
    <!-- Header -->
    <div
      class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-solid-2 flex-shrink-0"
    >
      <Button
        v-tooltip="$t('GENERAL.BACK')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        size="sm"
        @click="goBack"
      />
      <div class="flex flex-col min-w-0 flex-1">
        <span class="font-mono text-xs text-n-slate-11">
          {{ ticket.folio || `#${ticket.display_id}` }}
        </span>
        <span class="text-sm font-medium text-n-slate-12 truncate">
          {{ ticket.title }}
        </span>
      </div>
    </div>

    <!-- Scrollable Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Status & Priority Row -->
      <div class="flex gap-2 px-4 py-3 border-b border-n-weak">
        <!-- Status -->
        <div class="flex-1">
          <label class="text-xs text-n-slate-11 block mb-1.5">
            {{ $t('TICKETS.SIDEBAR.STATUS') }}
          </label>
          <div
            v-if="isTicketClosed"
            class="px-2.5 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12"
          >
            {{ statusConfig.label }}
          </div>
          <select
            v-else
            :value="currentStatus"
            :disabled="isUpdatingStatus"
            class="w-full px-2.5 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand disabled:opacity-50"
            @change="e => updateStatus(e.target.value)"
          >
            <option
              v-for="option in statusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ $t(option.label) }}
            </option>
          </select>
        </div>

        <!-- Priority -->
        <div class="flex-1">
          <label class="text-xs text-n-slate-11 block mb-1.5">
            {{ $t('TICKETS.SIDEBAR.PRIORITY') }}
          </label>
          <select
            :value="currentPriority"
            :disabled="isUpdatingPriority"
            class="w-full px-2.5 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand disabled:opacity-50"
            @change="e => updatePriority(e.target.value)"
          >
            <option
              v-for="option in priorityOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ $t(option.label) }}
            </option>
          </select>
        </div>
      </div>

      <!-- Verification Status -->
      <div
        v-if="verificationStatusLabel"
        class="flex items-center gap-2 px-4 py-2 border-b border-n-weak"
      >
        <span
          class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full"
          :class="verificationStatusClasses"
        >
          {{ verificationStatusLabel }}
        </span>
      </div>

      <!-- Assignee Section -->
      <div class="px-4 py-3 border-b border-n-weak">
        <label class="text-xs text-n-slate-11 block mb-1.5">
          {{ $t('TICKETS.SIDEBAR.ASSIGNEE') }}
        </label>
        <select
          :value="currentAssigneeId || ''"
          :disabled="isUpdatingAssignee"
          class="w-full px-2.5 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand disabled:opacity-50"
          @change="e => updateAssignee(e.target.value)"
        >
          <option value="">{{ $t('TICKETS.FORM.NO_ASSIGNEE') }}</option>
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>
        <div v-if="!currentAssigneeId && currentUser" class="mt-2 text-right">
          <Button
            variant="ghost"
            size="xs"
            color="blue"
            :is-loading="isUpdatingAssignee"
            @click="updateAssignee(currentUser.id)"
          >
            {{ $t('TICKETS.SIDEBAR.ASSIGN_TO_ME') }}
          </Button>
        </div>
      </div>

      <!-- Team Section -->
      <div class="px-4 py-3 border-b border-n-weak">
        <label class="text-xs text-n-slate-11 block mb-1.5">
          {{ $t('TICKETS.SIDEBAR.TEAM') }}
        </label>
        <select
          :value="currentTeamId || ''"
          :disabled="isUpdatingTeam"
          class="w-full px-2.5 py-1.5 text-sm rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand disabled:opacity-50"
          @change="e => updateTeam(e.target.value)"
        >
          <option value="">{{ $t('TICKETS.FORM.NO_TEAM') }}</option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
      </div>

      <!-- Description Section -->
      <div class="px-4 py-3 border-b border-n-weak">
        <h4 class="text-xs font-medium text-n-slate-11 mb-2">
          {{ $t('TICKETS.DETAILS.DESCRIPTION') }}
        </h4>
        <p
          v-if="ticket.description"
          class="text-sm text-n-slate-12 leading-relaxed max-h-24 overflow-y-auto"
        >
          {{ ticket.description }}
        </p>
        <p v-else class="text-sm text-n-slate-10 italic">
          {{ $t('TICKETS.DETAILS.NO_DESCRIPTION') }}
        </p>
      </div>

      <!-- Attachments Section -->
      <div class="px-4 py-3 border-b border-n-weak">
        <TicketAttachments
          :ticket="ticket"
          :readonly="isTicketClosed"
          @update="emit('update')"
        />
      </div>

      <!-- Details Grid -->
      <div class="px-4 py-3 border-b border-n-weak">
        <h4 class="text-xs font-medium text-n-slate-11 mb-3">
          {{ $t('TICKETS.SIDEBAR.DETAILS') }}
        </h4>
        <div class="grid grid-cols-2 gap-3">
          <!-- Channel -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.CHANNEL')
            }}</span>
            <div class="flex items-center gap-1.5 mt-0.5">
              <i :class="channelIcon" class="text-n-slate-11 text-sm" />
              <span class="text-sm text-n-slate-12 capitalize">
                {{ ticket.channel?.replace('_', ' ') || '-' }}
              </span>
            </div>
          </div>

          <!-- Type -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.TYPE')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ ticket.ticket_category?.name || ticket.ticket_type || '-' }}
            </span>
          </div>

          <!-- Service Type -->
          <div v-if="ticket.ticket_subcategory" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.SERVICE_TYPE')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ ticket.ticket_subcategory.name }}
            </span>
          </div>

          <!-- Client Name -->
          <div v-if="ticket.client_name" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.CLIENT')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5 truncate">
              {{ ticket.client_name }}
            </span>
          </div>

          <!-- Contract Number -->
          <div
            v-if="isContractsEnabled && ticket.contract_number"
            class="flex flex-col"
          >
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.CONTRACT')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5 font-mono">
              {{ ticket.contract_number }}
            </span>
          </div>

          <!-- Address fields -->
          <div v-if="ticket.address_street" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.ADDRESS_STREET')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ ticket.address_street }}
            </span>
          </div>
          <div v-if="ticket.address_colony" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.ADDRESS_COLONY')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ ticket.address_colony }}
            </span>
          </div>
          <div v-if="ticket.address_postal_code" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.ADDRESS_POSTAL_CODE')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ ticket.address_postal_code }}
            </span>
          </div>
          <div v-if="ticket.address_city" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.ADDRESS_CITY')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ ticket.address_city }}
            </span>
          </div>
          <div v-if="ticket.address_state" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.ADDRESS_STATE')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ ticket.address_state }}
            </span>
          </div>

          <!-- Created Date -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.CREATED')
            }}</span>
            <span class="text-sm text-n-slate-12 mt-0.5">
              {{ formatShortDate(ticket.created_at) }}
            </span>
          </div>

          <!-- SLA Deadline -->
          <div v-if="ticket.sla_deadline" class="flex flex-col">
            <span class="text-xs text-n-slate-10">{{
              $t('TICKETS.SIDEBAR.SLA')
            }}</span>
            <div class="flex items-center gap-1.5 mt-0.5">
              <i
                v-if="isOverdue"
                class="i-lucide-alert-circle text-n-ruby-11 text-sm"
              />
              <span
                class="text-sm"
                :class="
                  isOverdue ? 'text-n-ruby-11 font-medium' : 'text-n-slate-12'
                "
              >
                {{ formatShortDate(ticket.sla_deadline) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Activity Section -->
      <div class="px-4 py-3 border-b border-n-weak">
        <h4 class="text-xs font-medium text-n-slate-11 mb-3">
          {{ $t('TICKETS.DETAILS.ACTIVITY') }}
        </h4>

        <!-- Loading State -->
        <div
          v-if="isLoadingActivity"
          class="flex items-center justify-center py-4"
        >
          <Spinner size="small" />
        </div>

        <!-- Conversations List -->
        <div v-else class="flex flex-col gap-2 max-h-48 overflow-y-auto mb-3">
          <template v-for="conv in ticketConversations" :key="conv.id">
            <!-- Activity Message (system-generated) -->
            <div
              v-if="conv.message_type === 'activity'"
              v-tooltip.top="formatDateTime(conv.created_at)"
              class="flex justify-center"
            >
              <div
                class="px-2 py-1 rounded-md bg-n-alpha-1 text-n-slate-11 text-xs text-center"
              >
                {{ conv.message }}
              </div>
            </div>

            <!-- Regular Comment -->
            <div
              v-else
              class="flex gap-2 p-2 rounded-lg"
              :class="conv.private ? 'bg-n-amber-9/10' : 'bg-n-solid-3'"
            >
              <Avatar :name="conv.sender_name" :size="24" />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1 flex-wrap">
                  <span class="text-xs font-medium text-n-slate-12 truncate">
                    {{ conv.sender_name }}
                  </span>
                  <span class="text-xs text-n-slate-10">
                    {{ formatDateTime(conv.created_at) }}
                  </span>
                  <span
                    v-if="conv.private"
                    class="px-1.5 py-0.5 text-xs rounded bg-n-amber-9/20 text-n-amber-11"
                  >
                    {{ $t('TICKETS.ACTIONS_LABELS.PRIVATE') }}
                  </span>
                </div>
                <p class="text-xs text-n-slate-12 mt-0.5 break-words">
                  {{ conv.message }}
                </p>
              </div>
            </div>
          </template>

          <p
            v-if="!ticketConversations.length && !isLoadingActivity"
            class="text-xs text-n-slate-10 text-center py-2"
          >
            {{ $t('TICKETS.SIDEBAR.NO_ACTIVITY') }}
          </p>
        </div>

        <!-- Typing Indicator -->
        <div
          v-if="typingUserNames"
          class="text-xs text-n-slate-11 italic px-1 mb-1"
        >
          {{ typingUserNames }}
        </div>

        <!-- Add Comment -->
        <div class="flex flex-col gap-2">
          <div
            class="rounded-lg border border-n-weak bg-n-solid-3 p-2"
            :class="{ 'bg-n-amber-9/10 border-n-amber-9/30': isPrivate }"
          >
            <WootMessageEditor
              v-model="newComment"
              :editor-id="`ticket-sidebar-comment-${ticket.display_id}`"
              :placeholder="$t('TICKETS.DETAILS.ADD_COMMENT')"
              is-private
              :enable-canned-responses="false"
              :enable-variables="false"
              :focus-on-mount="false"
              class="ticket-comment-editor min-h-[40px] [&_.ProseMirror]:max-h-[120px] [&_.ProseMirror]:overflow-y-auto"
              @typing-on="onCommentInput"
              @typing-off="sendTypingOff"
            />
          </div>
          <div class="flex items-center justify-between">
            <label
              class="flex items-center gap-1.5 text-xs text-n-slate-11 cursor-pointer"
            >
              <input
                v-model="isPrivate"
                type="checkbox"
                class="rounded w-3 h-3"
              />
              {{ $t('TICKETS.DETAILS.PRIVATE_NOTE') }}
            </label>
            <Button
              size="sm"
              :disabled="!newComment.trim() || isSubmitting"
              :loading="isSubmitting"
              @click="addComment"
            >
              {{ $t('TICKETS.DETAILS.SEND') }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div
      class="flex flex-col gap-2 px-4 py-3 border-t border-n-weak bg-n-solid-2 flex-shrink-0"
    >
      <Button
        v-if="canResolve"
        color="teal"
        class="w-full justify-center"
        @click="openResolveModal"
      >
        {{ $t('TICKETS.ACTIONS.RESOLVE') }}
      </Button>
      <div v-if="isVerificationPending" class="flex gap-2">
        <Button
          color="teal"
          size="sm"
          class="flex-1 justify-center"
          @click="verifyTicket"
        >
          {{ $t('TICKETS.VERIFICATION.MARK_VERIFIED') }}
        </Button>
        <Button
          variant="outline"
          size="sm"
          class="flex-1 justify-center"
          @click="rejectVerification"
        >
          {{ $t('TICKETS.VERIFICATION.MARK_REJECTED') }}
        </Button>
      </div>
      <Button
        variant="outline"
        icon="i-lucide-expand"
        class="w-full justify-center"
        @click="viewFullDetails"
      >
        {{ $t('TICKETS.SIDEBAR.VIEW_FULL_DETAILS') }}
      </Button>
    </div>

    <!-- Resolve Modal -->
    <ResolveTicketModal
      v-if="showResolveModal"
      :show="showResolveModal"
      :ticket="ticket"
      @close="showResolveModal = false"
      @resolved="onResolved"
    />
  </div>
</template>
